package com.evoapps.evoshift.connectivity

import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.transformLatest
import kotlinx.coroutines.ExperimentalCoroutinesApi

/**
 * SH-168: whether the device has a connection. Firestore's offline persistence already queues the
 * writes made without one; this only lets the app say so (the shell's offline banner).
 * ConnectivityManager on Android, the Network framework's path monitor on iOS, the browser's
 * online / offline events on the web, a reachability check every 30 s on the desktop.
 */
interface ConnectivityWatcher {
    /** True while the device has a usable connection (assumed until the platform says otherwise). */
    val isOnline: StateFlow<Boolean>
}

/** A connection set by hand: tests, and platforms that cannot tell (always online). */
class ManualConnectivity(online: Boolean = true) : ConnectivityWatcher {
    private val state = MutableStateFlow(online)
    override val isOnline: StateFlow<Boolean> = state.asStateFlow()

    fun set(online: Boolean) {
        state.value = online
    }
}

expect fun platformConnectivityWatcher(): ConnectivityWatcher

/** What the shell's banner says about the connection. */
enum class ConnectionNotice {
    /** Online all along (or long enough ago): no banner. */
    NONE,
    /** "No connection. Changes are kept on this device." */
    OFFLINE,
    /** "Back online", for [BACK_ONLINE_MILLIS] after the connection returned. */
    BACK_ONLINE
}

/** How long "Back online" stays after the connection returned. */
const val BACK_ONLINE_MILLIS = 3_000L

/**
 * The banner's notices from the connection: [ConnectionNotice.OFFLINE] while offline,
 * [ConnectionNotice.BACK_ONLINE] for [backOnlineMillis] once it returns, then nothing. Starting
 * online shows nothing.
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun Flow<Boolean>.connectionNotices(backOnlineMillis: Long = BACK_ONLINE_MILLIS): Flow<ConnectionNotice> {
    var wasOffline = false
    return distinctUntilChanged().transformLatest { online ->
        when {
            !online -> {
                wasOffline = true
                emit(ConnectionNotice.OFFLINE)
            }
            wasOffline -> {
                wasOffline = false
                emit(ConnectionNotice.BACK_ONLINE)
                delay(backOnlineMillis)
                emit(ConnectionNotice.NONE)
            }
            else -> emit(ConnectionNotice.NONE)
        }
    }
}
